import type { AppRouter } from "@runcheetah/api/router";
import type { inferRouterOutputs } from "@trpc/server";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";

import { SocialPostPreview } from "@/components/campaign/social-post-preview";
import type { AssistantSocialPostPreviewReference } from "@/lib/assistant-social-post-previews";
import { resolveScheduledPostPreview } from "@/lib/scheduled-post-preview";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type CampaignPost = RouterOutputs["campaign"]["get"]["scheduledPosts"][number];
type StandalonePost = RouterOutputs["standaloneAssetBatch"]["get"]["scheduledPosts"][number];

export function AssistantSocialPostPreviews({
  brandId,
  profileLabel,
  references,
}: {
  brandId: string;
  profileLabel: string;
  references: AssistantSocialPostPreviewReference[];
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {references.map((reference) =>
        reference.ownerType === "campaign" ? (
          <AssistantCampaignSocialPostPreview
            key={reference.scheduledPostId}
            brandId={brandId}
            profileLabel={profileLabel}
            reference={reference}
          />
        ) : (
          <AssistantStandaloneSocialPostPreview
            key={reference.scheduledPostId}
            brandId={brandId}
            profileLabel={profileLabel}
            reference={reference}
          />
        ),
      )}
    </div>
  );
}

function AssistantCampaignSocialPostPreview({
  brandId,
  profileLabel,
  reference,
}: {
  brandId: string;
  profileLabel: string;
  reference: AssistantSocialPostPreviewReference;
}) {
  const query = trpc.campaign.get.useQuery(
    { brandId, id: reference.ownerId },
    { staleTime: 30_000 },
  );
  const post = query.data?.scheduledPosts.find((item) => item.id === reference.scheduledPostId);

  return (
    <AssistantResolvedSocialPostPreview
      isLoading={query.isPending}
      isError={query.isError}
      ownerName={query.data?.name ?? null}
      post={post ?? null}
      profileLabel={profileLabel}
      reference={reference}
    />
  );
}

function AssistantStandaloneSocialPostPreview({
  brandId,
  profileLabel,
  reference,
}: {
  brandId: string;
  profileLabel: string;
  reference: AssistantSocialPostPreviewReference;
}) {
  const query = trpc.standaloneAssetBatch.get.useQuery(
    { brandId, id: reference.ownerId },
    { staleTime: 30_000 },
  );
  const post = query.data?.scheduledPosts.find((item) => item.id === reference.scheduledPostId);

  return (
    <AssistantResolvedSocialPostPreview
      isLoading={query.isPending}
      isError={query.isError}
      ownerName={query.data?.name ?? null}
      post={post ?? null}
      profileLabel={profileLabel}
      reference={reference}
    />
  );
}

function AssistantResolvedSocialPostPreview({
  isLoading,
  isError,
  ownerName,
  post,
  profileLabel,
  reference,
}: {
  isLoading: boolean;
  isError: boolean;
  ownerName: string | null;
  post: CampaignPost | StandalonePost | null;
  profileLabel: string;
  reference: AssistantSocialPostPreviewReference;
}) {
  const intl = useIntl();
  const preview = post
    ? resolveScheduledPostPreview({ output: post.output, draft: post.draft })
    : null;
  const plan = preview?.plan ?? null;
  const scheduledLabel = post
    ? intl.formatDate(new Date(post.scheduledFor), {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return (
    <article className="overflow-hidden rounded-[2rem] border border-border/80 bg-card shadow-[0_24px_70px_-45px_rgba(19,34,49,0.55)]">
      <div className="flex items-start justify-between gap-3 border-b border-border/70 px-4 py-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/48">
            <FormattedMessage id="assistantSocialPostPreview.label" defaultMessage="Post preview" />
          </div>
          <div className="mt-1 truncate text-sm font-semibold text-foreground">
            {ownerName ?? (
              <FormattedMessage
                id="assistantSocialPostPreview.ownerFallback"
                defaultMessage="Social post"
              />
            )}
          </div>
          {scheduledLabel ? (
            <div className="text-xs text-foreground/55">{scheduledLabel}</div>
          ) : null}
        </div>
        {reference.href ? (
          <Link
            to={reference.href}
            className="shrink-0 rounded-full border border-border/80 px-3 py-1 text-xs font-semibold text-foreground/72 hover:border-foreground/25 hover:text-foreground"
          >
            <FormattedMessage id="assistantSocialPostPreview.open" defaultMessage="Open" />
          </Link>
        ) : null}
      </div>

      <div className="bg-[#f7f7f4] p-3">
        {isLoading ? (
          <AssistantPreviewState className="animate-pulse">
            <FormattedMessage
              id="assistantSocialPostPreview.loading"
              defaultMessage="Loading preview..."
            />
          </AssistantPreviewState>
        ) : isError ? (
          <AssistantPreviewState>
            <FormattedMessage
              id="assistantSocialPostPreview.loadFailed"
              defaultMessage="Could not load this preview."
            />
          </AssistantPreviewState>
        ) : plan && preview ? (
          <SocialPostPreview
            renderMode="preview"
            platform={plan.platform}
            type={plan.postType}
            plan={plan}
            linkedAssets={preview.linkedAssets}
            brandkit={preview.brandkit}
            profileLabel={profileLabel}
            caption={preview.caption}
            hashtags={preview.hashtags}
            maxWidth={360}
            className="mx-auto"
          />
        ) : (
          <AssistantPreviewState>
            <FormattedMessage
              id="assistantSocialPostPreview.notReady"
              defaultMessage="This post does not have a renderable creative yet."
            />
          </AssistantPreviewState>
        )}
      </div>
    </article>
  );
}

function AssistantPreviewState({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-52 items-center justify-center rounded-3xl border border-dashed border-border/80 bg-background/75 px-6 text-center text-sm text-foreground/58",
        className,
      )}
    >
      {children}
    </div>
  );
}
